/**
 * Primeiros passos do Dashboard — ponto de partida do assistente de
 * configuração (P1 pago / F1 Free, out/2026 — proposta do Bruno).
 *
 * Cada passo abre o modal da tarefa no próprio Dashboard e o status vem dos
 * DADOS (UCs, faturas, `lastInboundAt`/`inboundSetupAt`, plantas), não de um
 * campo de progresso:
 * - Pago: 1) fatura · 2) e-mail automático (a fazer / aguardando / concluído) ·
 *   3) inversor (só se alguma fatura indica geração).
 * - Free: 1) fatura · 2) geração do último mês informada à mão (se há geração)
 *   + linha de benefício dos planos pagos (não conta no progresso).
 * C4: o antigo passo "Envie pelo menos 3 faturas" levava o Free para o upload
 * em lote, que redireciona para planos — e uma fatura já traz 12 meses.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BsCheckCircleFill,
  BsCircle,
  BsClockFill,
  BsStars,
  BsX,
} from 'react-icons/bs';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import { selectInvoiceList } from 'store/invoice/invoice.selector';
import { currentUser } from 'store/user/user.selector';
import * as selectMonitoringSystemUser from 'store/monitoring_system_user/monitoring_system_user.selector';
import { setModalInvoiceState } from 'store/modal_invoice/modal_invoice.slice';

import { canAutoReceiveInvoice, canIntegrateMonitoring } from 'utils/planRules';
import { format } from 'utils/format';

import { InboundEmailWizard } from 'components/inbound-email/inbound-email-wizard.component';
import { ModalAssociateMonitoringSystem } from 'components/modal-associate-monitoring-system/modal-associate-monitoring-system.component';
import { StatusTag } from 'components/setup-wizard/setup-wizard.styles';

import {
  ChecklistCard,
  ChecklistHeader,
  ChecklistTitle,
  DismissButton,
  ProgressTrack,
  ProgressFill,
  StepRow,
  StepIconBox,
  StepBody,
  StepTitle,
  StepHint,
  StepAction,
  HelpLink,
} from './onboarding-checklist.styles';

const DISMISS_KEY = 'energypass_onboarding_dismissed';

type StepState = 'done' | 'waiting' | 'todo';

interface Step {
  id: string;
  title: string;
  hint: string;
  state: StepState;
  actionLabel?: string;
  action?: () => void;
  helpTopic?: string;
  /** Linha de benefício (Free): não conta no progresso. */
  perk?: boolean;
}

const STATE_LABEL: Record<StepState, string> = {
  done: 'concluído',
  waiting: 'aguardando',
  todo: 'a fazer',
};

export const OnboardingChecklist = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(currentUser);
  const units = useAppSelector(selectConsumerUnitList);
  const invoiceList = useAppSelector(selectInvoiceList);
  const monitoringList = useAppSelector(selectMonitoringSystemUser.list);

  const plan = user?.plan?.name ?? 'Free';
  const paid = canIntegrateMonitoring(plan);
  const autoReceive = canAutoReceiveInvoice(plan);

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [emailOpen, setEmailOpen] = useState(false);
  const [inverterOpen, setInverterOpen] = useState(false);

  const firstUnit = units[0];
  const fvUnit = units.find((uc) => uc.hasFv);
  const hasMonitoredPlant = units.some((uc) =>
    (uc.plantList ?? []).some(
      (p) => !!p.monitoringSystemUser?.id && !!p.identifier
    )
  );
  const hasMonitoring = monitoringList.length > 0 || hasMonitoredPlant;

  // Fatura mais recente da 1ª UC (texto "criada com a fatura de set/2026")
  const firstInvoice = useMemo(
    () =>
      invoiceList
        .filter((inv) => inv.consumerUnit?.id === firstUnit?.id)
        .sort((a, b) => (a.refMonth < b.refMonth ? 1 : -1))[0],
    [invoiceList, firstUnit]
  );

  // Free: o mês mais recente da UC com geração já tem a geração informada?
  const fvLatestRefMonth = useMemo(
    () =>
      invoiceList
        .filter((inv) => inv.consumerUnit?.id === fvUnit?.id)
        .map((inv) => inv.refMonth)
        .sort()
        .pop(),
    [invoiceList, fvUnit]
  );
  const fvGenerationInformed =
    !!fvLatestRefMonth &&
    (fvUnit?.monthGenerationList ?? []).some(
      (g) => g.refMonth === fvLatestRefMonth && (g.value ?? 0) > 0
    );

  const emailState: StepState = units.some((uc) => uc.lastInboundAt)
    ? 'done'
    : units.some((uc) => uc.inboundSetupAt)
    ? 'waiting'
    : 'todo';

  const invoiceStep: Step = {
    id: 'invoice',
    title: 'Envie a primeira fatura',
    hint: firstUnit
      ? `${firstUnit.name} (UC ${firstUnit.installation}) criada${
          firstInvoice
            ? ` com a fatura de ${format.refMonth(firstInvoice.refMonth)}`
            : ''
        }.${paid ? '' : ' Todo mês, importe a conta nova em PDF.'}`
      : 'Uma fatura em PDF basta: ela cria o imóvel e traz até 12 meses de histórico.',
    state: units.length > 0 ? 'done' : 'todo',
    actionLabel: 'Enviar fatura',
    action: () =>
      dispatch(setModalInvoiceState({ modalState: 'select_invoice' })),
    helpTopic: 'pp-fatura',
  };

  const steps: Step[] = paid
    ? [
        invoiceStep,
        ...(autoReceive
          ? [
              {
                id: 'email',
                title: 'Receba as faturas automaticamente',
                hint:
                  emailState === 'waiting'
                    ? 'Endereço cadastrado. A primeira fatura chega no próximo ciclo, em até 30 dias.'
                    : 'Cadastre o seu endereço EnergyPass na distribuidora e pare de baixar o PDF todo mês.',
                state: emailState,
                actionLabel: emailState === 'waiting' ? 'Ver' : 'Configurar',
                action: () => setEmailOpen(true),
                helpTopic: 'pp-email',
              } as Step,
            ]
          : []),
        ...(fvUnit || hasMonitoring
          ? [
              {
                id: 'inverter',
                title: 'Conecte seu inversor solar',
                hint: fvUnit
                  ? `Detectamos geração solar em ${fvUnit.name}. Use o login do app em que você acompanha a geração.`
                  : 'Use o login do app em que você acompanha a geração.',
                state: hasMonitoring ? 'done' : 'todo',
                actionLabel: 'Conectar',
                action: () => setInverterOpen(true),
                helpTopic: 'pp-inversor',
              } as Step,
            ]
          : []),
      ]
    : [
        invoiceStep,
        ...(fvUnit && fvLatestRefMonth
          ? [
              {
                id: 'generation',
                title: `Informe a geração solar de ${format.refMonth(
                  fvLatestRefMonth
                )}`,
                hint: 'Veja no app do inversor quanto o sistema gerou no período da fatura e digite o número.',
                state: fvGenerationInformed ? 'done' : 'todo',
                actionLabel: 'Informar',
                action: () =>
                  navigate(`/unidades-consumidoras/${fvUnit.id}?aba=geracao`),
                helpTopic: 'pp-geracao',
              } as Step,
            ]
          : []),
        {
          id: 'perk',
          title: 'Fatura e geração no automático',
          hint: 'Nos planos pagos, a fatura chega sozinha da distribuidora e a geração vem direto do inversor, com aviso se o sistema parar.',
          state: 'todo',
          actionLabel: 'Ver planos',
          action: () => navigate('/conta'),
          perk: true,
        },
      ];

  const counted = steps.filter((s) => !s.perk);
  const doneCount = counted.filter((s) => s.state === 'done').length;
  const allDone = doneCount === counted.length;

  if (dismissed || allDone) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* armazenamento indisponível — some só nesta visita */
    }
    setDismissed(true);
  };

  return (
    <ChecklistCard>
      <ChecklistHeader>
        <ChecklistTitle>
          Primeiros passos ({doneCount}/{counted.length})
        </ChecklistTitle>
        <DismissButton
          type="button"
          onClick={handleDismiss}
          title="Não mostrar novamente"
        >
          <BsX size={22} />
        </DismissButton>
      </ChecklistHeader>

      <ProgressTrack>
        <ProgressFill $pct={counted.length ? doneCount / counted.length : 0} />
      </ProgressTrack>

      {steps.map((step) => {
        const done = step.state === 'done';
        return (
          <StepRow key={step.id} $done={done}>
            <StepIconBox $done={done}>
              {step.perk ? (
                <BsStars size={20} />
              ) : done ? (
                <BsCheckCircleFill size={20} />
              ) : step.state === 'waiting' ? (
                <BsClockFill size={20} />
              ) : (
                <BsCircle size={20} />
              )}
            </StepIconBox>
            <StepBody>
              <StepTitle $done={done}>{step.title}</StepTitle>
              <StepHint>
                {step.hint}{' '}
                {!done && step.helpTopic && (
                  <HelpLink
                    type="button"
                    onClick={() => navigate(`/ajuda?topico=${step.helpTopic}`)}
                  >
                    Ver como fazer
                  </HelpLink>
                )}
              </StepHint>
            </StepBody>
            {!step.perk && (
              <StatusTag
                tone={
                  done ? 'good' : step.state === 'waiting' ? 'warn' : 'muted'
                }
              >
                {STATE_LABEL[step.state]}
              </StatusTag>
            )}
            {!done && step.action && (
              <StepAction type="button" onClick={step.action}>
                {step.actionLabel}
              </StepAction>
            )}
          </StepRow>
        );
      })}

      <InboundEmailWizard
        show={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultConsumerUnitId={firstUnit?.id}
        stepLabel={`Passo 2 de ${counted.length}`}
      />
      <ModalAssociateMonitoringSystem
        showModal={inverterOpen}
        hideModal={() => setInverterOpen(false)}
        defaultConsumerUnitId={fvUnit?.id}
        stepLabel={`Passo ${counted.length} de ${counted.length}`}
      />
    </ChecklistCard>
  );
};
